/**
 * Structured incident timeline cards + camera snapshot evidence (ADV Wave 2).
 */

function escapeHtml(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeHtmlAttr(v) {
  return escapeHtml(v);
}

const KIND_LABELS = {
  event: "Event",
  ai_incident: "Smart alert",
  dispatch: "Response",
  command: "Command",
  camera: "Camera",
  notification: "Notification",
  audit: "Audit",
};

function softStatusLabel(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";
  const map = {
    open: "Open",
    closed: "Closed",
    resolved: "Resolved",
    pending: "Pending",
    active: "Active",
    ack: "Acknowledged",
    acknowledged: "Acknowledged",
    dispatched: "Dispatched",
    failed: "Failed",
  };
  if (map[s]) return map[s];
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function kindBadgeClass(kind) {
  const k = String(kind || "").toLowerCase();
  if (k === "dispatch" || k === "command") return "badge warn";
  if (k === "ai_incident") return "badge offline";
  if (k === "audit") return "badge neutral";
  if (k === "camera" || k === "notification") return "badge ok";
  return "badge";
}

function detailActor(detail) {
  const d = detail && typeof detail === "object" ? detail : {};
  return (
    d.actor ||
    d.username ||
    d.assigned_responder ||
    d.responder_username ||
    d.responder ||
    ""
  );
}

function detailCorrelation(detail) {
  const d = detail && typeof detail === "object" ? detail : {};
  return d.correlation_id || d.correlation || d.alarm_id || "";
}

function evidenceLinksForItem(it) {
  const d = (it && it.detail) || {};
  const links = [];
  const dispatchId = String(d.dispatch_id || d.id || "").trim();
  if (dispatchId) {
    links.push({
      dest: `response?dispatch_id=${encodeURIComponent(dispatchId)}`,
      label: "Open response",
    });
  }
  const eventId = d.event_id != null ? String(d.event_id) : "";
  if (eventId) {
    links.push({
      dest: "incidents",
      id: eventId,
      label: "Incident",
    });
  }
  const snapId = String(d.snapshot_id || "").trim();
  if (snapId) {
    links.push({
      dest: "snapshots", // #/snapshots
      id: snapId,
      label: "Snapshot",
    });
  }
  if (String(it.kind || "") === "ai_incident" && d.incident_id) {
    links.push({
      dest: "overview",
      label: "Home",
    });
  }
  return links;
}

/**
 * @param {object|null|undefined} snapshot
 * @returns {string}
 */
export function buildSnapshotEvidenceHtml(snapshot) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : null;
  if (!snap) {
    return `<div class="glass-panel glass-panel--inset incident-snapshot-evidence" style="padding:12px;margin-top:12px">
      <strong>Camera evidence</strong>
      <p class="muted" style="margin-top:6px">No snapshot linked to this incident yet.</p>
    </div>`;
  }
  const snapId = String(snap.snapshot_id || "").trim();
  const expired = !!snap.is_expired;
  const hasImage = !!snap.has_image && !expired;
  const imageUrl = String(snap.image_url || snap.signed_image_url || "").trim();
  let statusLine = "Snapshot details are on file.";
  if (expired) {
    statusLine = "This snapshot was cleared — evidence is no longer available.";
  } else if (!snap.has_image) {
    statusLine = "Snapshot is recorded, but the image is not ready yet.";
  } else if (hasImage && imageUrl) {
    statusLine = "Linked camera snapshot is available.";
  }
  const galleryLink = snapId
    ? `<button type="button" class="btn sm secondary btn-tap" data-cc-go="snapshots" data-cc-id="${escapeHtmlAttr(snapId)}">Open snapshots</button>`
    : `<button type="button" class="btn sm secondary btn-tap" data-cc-go="snapshots">Open snapshots</button>`;
  const imgBlock = hasImage && imageUrl
    ? `<p style="margin-top:8px"><img src="${escapeHtmlAttr(imageUrl)}" alt="Camera snapshot" style="max-width:100%;max-height:220px;border-radius:8px" loading="lazy" /></p>`
    : "";
  return `<div class="glass-panel glass-panel--inset incident-snapshot-evidence" style="padding:12px;margin-top:12px">
    <div class="row" style="justify-content:space-between;gap:8px;align-items:center;flex-wrap:wrap">
      <strong>Camera evidence</strong>
      ${expired ? '<span class="badge offline">Cleared</span>' : (hasImage ? '<span class="badge ok">Available</span>' : '<span class="badge neutral">Pending</span>')}
    </div>
    <p class="muted" style="margin-top:6px">${escapeHtml(statusLine)}</p>
    ${imgBlock}
    <p style="margin-top:8px">${galleryLink}</p>
  </div>`;
}

/**
 * @param {Array<object>} items
 * @param {{ snapshot?: object|null, eventId?: string|number }} [opts]
 * @returns {string}
 */
export function buildIncidentTimelineCardsHtml(items, opts) {
  const list = Array.isArray(items) ? items : [];
  const options = opts || {};
  if (!list.length) {
    return `<div class="incident-timeline-cards"><p class="muted">No timeline items yet.</p></div>`;
  }
  const cards = list.map((it) => {
    const kind = String(it.kind || "event");
    const label = KIND_LABELS[kind] || kind;
    const detail = it.detail || {};
    const actor = detailActor(detail);
    const correlation = detailCorrelation(detail);
    const links = evidenceLinksForItem(it);
    const linksHtml = links.length
      ? `<div class="row" style="gap:6px;flex-wrap:wrap;margin-top:6px">${links.map((l) =>
          `<button type="button" class="btn sm secondary btn-tap" data-cc-go="${escapeHtmlAttr(l.dest)}"${l.id ? ` data-cc-id="${escapeHtmlAttr(l.id)}"` : ""}>${escapeHtml(l.label)}</button>`,
        ).join("")}</div>`
      : "";
    const extra = [];
    if (detail.status) extra.push(escapeHtml(softStatusLabel(detail.status)));
    if (detail.dispatch_id && !links.some((l) => l.label === "Open response")) {
      extra.push("Response linked");
    }
    if (detail.audit_id) extra.push("Audit on file");
    return `<article class="glass-panel glass-panel--inset incident-timeline-card" data-timeline-kind="${escapeHtmlAttr(kind)}" style="padding:10px 12px;margin-bottom:8px">
      <div class="row" style="justify-content:space-between;gap:8px;align-items:flex-start;flex-wrap:wrap">
        <span class="${kindBadgeClass(kind)}">${escapeHtml(label)}</span>
        <span class="mono muted" style="font-size:12px">${escapeHtml(String(it.at || ""))}</span>
      </div>
      <p style="margin-top:6px"><strong>${escapeHtml(String(it.summary || kind))}</strong></p>
      ${actor ? `<p class="muted" style="font-size:12px;margin-top:4px">By ${escapeHtml(String(actor))}</p>` : ""}
      ${correlation ? `<p class="muted" style="font-size:12px;margin-top:2px">Linked to another alert</p>` : ""}
      ${extra.length ? `<p class="muted" style="font-size:12px;margin-top:4px">${extra.join(" · ")}</p>` : ""}
      ${linksHtml}
    </article>`;
  }).join("");
  let snapshotBlock = "";
  if (options.snapshot) {
    snapshotBlock = buildSnapshotEvidenceHtml(options.snapshot);
  }
  return `<div class="incident-timeline-cards">${cards}</div>${snapshotBlock}`;
}
